import React, { useEffect, useMemo, useState } from 'react';
import {
  AlertTriangle,
  CheckCircle2,
  ClipboardList,
  Film,
  Loader2,
  RefreshCw,
  Sparkles,
  Target,
  Trash2,
  Upload,
  Wand2
} from 'lucide-react';
import {
  REEL_ACCEPT,
  REEL_ALLOWED_TYPES,
  REEL_MAX_UPLOAD_BYTES,
  analyzeReel,
  createReelUploadUrl,
  deleteReelInsight,
  getReelInsightsConfig,
  listReelInsights,
  reelInsightModes,
  uploadReelToSignedUrl
} from './lib/reelInsightsApi';

const formatBytes = (bytes) => {
  if (!bytes && bytes !== 0) return '-';
  if (bytes >= 1024 * 1024) return `${(bytes / 1024 / 1024).toFixed(1)} MB`;
  if (bytes >= 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${bytes} B`;
};

const formatDate = (value) => {
  if (!value) return 'Unknown';
  return new Date(value).toLocaleString('en-GB', {
    day: 'numeric',
    month: 'short',
    hour: '2-digit',
    minute: '2-digit'
  });
};

const asArray = (value) => Array.isArray(value) ? value.filter(Boolean) : [];

const getModeLabel = (mode) => {
  return reelInsightModes.find((item) => item.key === mode)?.label || mode;
};

const getReportScore = (report) => {
  const score = report?.score ?? report?.analysis?.score;
  return Number.isFinite(Number(score)) ? Number(score) : null;
};

const getStatusClass = (status) => {
  if (status === 'complete') return 'text-green-300 bg-green-500/10 border-green-500/30';
  if (status === 'failed') return 'text-red-300 bg-red-500/10 border-red-500/30';
  return 'text-yellow-200 bg-yellow-500/10 border-yellow-500/30';
};

function ScoreCircle({ score, mode }) {
  const hasScore = Number.isFinite(score);
  const label = mode === 'viral_assessment' ? 'Viral score' : 'Value score';

  return (
    <div className="flex items-center gap-4">
      <div className="relative h-24 w-24 shrink-0 rounded-full border border-purple-500/40 bg-purple-500/10 flex items-center justify-center">
        <div className="text-center">
          <div className="text-3xl font-bold text-white">{hasScore ? Math.round(score) : '-'}</div>
          <div className="text-[10px] uppercase tracking-wider text-purple-200">/100</div>
        </div>
      </div>
      <div>
        <p className="text-xs uppercase tracking-wider text-purple-300">{label}</p>
        <p className="text-sm text-gray-400 mt-1">
          {mode === 'viral_assessment'
            ? 'Scored against strict viral mechanics. Anything above 80 should feel genuinely strong.'
            : 'Useful for extracting reusable creative strategy from an existing reel.'}
        </p>
      </div>
    </div>
  );
}

function SuggestedCtaCard({ cta }) {
  if (!cta) return null;
  const fields = typeof cta === 'string'
    ? [['Recommended CTA', cta]]
    : [
        ['Primary', cta.primary],
        ['On-screen text', cta.onScreenText],
        ['Spoken line', cta.spokenLine],
        ['Caption line', cta.captionLine],
        ['Placement', cta.placement],
        ['Why it helps', cta.why]
      ].filter(([, value]) => value);
  if (!fields.length) return null;
  return (
    <div className="rounded-xl border border-green-500/30 bg-green-500/10 p-4">
      <div className="flex items-center gap-2 text-green-200 mb-3">
        <Target size={17} />
        <h3 className="font-semibold">Suggested CTA</h3>
      </div>
      <div className="grid grid-cols-1 gap-3 md:grid-cols-2">
        {fields.map(([label, value]) => (
          <div key={label} className="rounded-lg border border-green-500/20 bg-gray-950/30 p-3">
            <p className="text-[10px] uppercase tracking-wider text-green-300">{label}</p>
            <p className="mt-1 text-sm leading-relaxed text-green-50">{value}</p>
          </div>
        ))}
      </div>
    </div>
  );
}

function WhyItWorksCard({ analysis }) {
  const detailedItems = Array.isArray(analysis?.whyItWorks) ? analysis.whyItWorks : [];
  const fields = detailedItems.length ? [] : [
    ['Observed action', analysis?.observedAction],
    ['Mechanism', analysis?.mechanism],
    ['Why it works', analysis?.whyItWorks],
    ['Repeatable lesson', analysis?.repeatableLesson]
  ].filter(([, value]) => value);

  if (!fields.length && !detailedItems.length) return null;

  return (
    <div className="rounded-xl border border-gray-800 bg-gray-900 p-5">
      <div className="flex items-center gap-2 text-purple-200 mb-4">
        <Wand2 size={18} />
        <h3 className="font-semibold">What Makes It Work</h3>
      </div>
      {detailedItems.length ? (
        <div className="space-y-3">
          {detailedItems.map((item, index) => (
            <div key={`${item.time || 'moment'}-${index}`} className="rounded-lg border border-gray-800 bg-gray-950/70 p-4">
              <p className="text-sm font-semibold text-white">{item.time ? `${item.time} · ` : ''}{item.observedAction || item.action || `Moment ${index + 1}`}</p>
              <div className="mt-3 grid grid-cols-1 gap-3 md:grid-cols-3">
                {[
                  ['Mechanism', item.mechanism],
                  ['Why it works', item.whyItWorks || item.why || item.effect],
                  ['Repeatable lesson', item.repeatableLesson || item.lesson]
                ].filter(([, value]) => value).map(([label, value]) => (
                  <div key={label}>
                    <p className="text-[10px] uppercase tracking-wider text-gray-500">{label}</p>
                    <p className="mt-1 text-sm leading-relaxed text-gray-200">{value}</p>
                  </div>
                ))}
              </div>
            </div>
          ))}
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {fields.map(([label, value]) => (
            <div key={label} className="rounded-lg border border-gray-800 bg-gray-950/70 p-4">
              <p className="text-xs uppercase tracking-wider text-gray-500 mb-2">{label}</p>
              <p className="text-sm text-gray-200 leading-relaxed">{value}</p>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

function StrategyReferencesCard({ references }) {
  const items = asArray(references);
  if (!items.length) return null;

  return (
    <div className="rounded-xl border border-blue-500/30 bg-blue-500/10 p-4">
      <div className="flex items-center gap-2 text-blue-200 mb-3">
        <Sparkles size={17} />
        <h3 className="font-semibold">Strategy References</h3>
      </div>
      <ul className="space-y-2 text-sm text-blue-50">
        {items.map((item, index) => (
          <li key={`${item.label || item}-${index}`}>
            {typeof item === 'string' ? item : (
              <>
                <span className="font-medium">{item.reference || item.label || item.title || 'Reference'}</span>
                {item.evidence && <span className="block text-xs text-blue-100/70 mt-1">Evidence: {item.evidence}</span>}
                {(item.howItExplainsPerformance || item.explanation || item.note || item.reason) && (
                  <span className="block text-xs text-blue-100/70 mt-1">{item.howItExplainsPerformance || item.explanation || item.note || item.reason}</span>
                )}
                {(item.action || item.recommendation) && <span className="block text-xs text-blue-100 mt-1">Action: {item.action || item.recommendation}</span>}
              </>
            )}
          </li>
        ))}
      </ul>
    </div>
  );
}

function ReportList({ reports, selectedId, onSelect, onDelete, isDeleting }) {
  if (!reports.length) {
    return (
      <div className="rounded-xl border border-dashed border-gray-700 bg-gray-900 p-8 text-center">
        <Film size={28} className="mx-auto mb-3 text-gray-500" />
        <p className="text-sm text-gray-400">No reel reports yet.</p>
      </div>
    );
  }

  return (
    <div className="space-y-3">
      {reports.map((report) => {
        const score = getReportScore(report);
        const selected = selectedId === report.id;
        return (
          <div
            key={report.id}
            className={`w-full rounded-xl border p-4 text-left transition-colors ${
              selected
                ? 'border-purple-500/60 bg-purple-500/10'
                : 'border-gray-800 bg-gray-900 hover:border-gray-700'
            }`}
          >
            <div className="flex items-start justify-between gap-3">
              <div className="min-w-0">
                <p className="font-semibold text-white truncate">{report.title}</p>
                <p className="text-xs text-gray-500 mt-1">{getModeLabel(report.mode)} · {formatDate(report.created_at)}</p>
              </div>
              <span className={`rounded-full border px-2 py-1 text-[11px] ${getStatusClass(report.status)}`}>
                {report.status}
              </span>
            </div>
            <div className="mt-3 flex items-center justify-between gap-3">
              <span className="text-xs text-gray-500">{report.file_size ? formatBytes(report.file_size) : 'Saved report'}</span>
              <div className="flex items-center gap-2">
                {score !== null && <span className="text-sm font-bold text-purple-200">{Math.round(score)}/100</span>}
                <button
                  type="button"
                  onClick={() => onSelect(report)}
                  className="rounded-lg border border-gray-700 px-3 py-1.5 text-xs text-gray-300 hover:border-purple-500/40 hover:text-purple-200"
                >
                  View
                </button>
                <button
                  type="button"
                  onClick={() => onDelete(report)}
                  className="inline-flex h-8 w-8 items-center justify-center rounded-lg border border-gray-700 text-gray-400 hover:border-red-500/40 hover:text-red-300"
                >
                  {isDeleting === report.id ? <Loader2 size={15} className="animate-spin" /> : <Trash2 size={15} />}
                </button>
              </div>
            </div>
          </div>
        );
      })}
    </div>
  );
}

function TextList({ title, icon: Icon, items, color = 'text-gray-200' }) {
  const list = asArray(items);
  if (!list.length) return null;

  return (
    <div className="rounded-xl border border-gray-800 bg-gray-900 p-5">
      <div className={`flex items-center gap-2 mb-4 ${color}`}>
        {Icon && <Icon size={17} />}
        <h3 className="font-semibold">{title}</h3>
      </div>
      <ul className="space-y-3 text-sm text-gray-300 leading-relaxed">
        {list.map((item, index) => (
          <li key={`${title}-${index}`}>
            {typeof item === 'string'
              ? item
              : `${item.time ? `${item.time}: ` : ''}${item.beat || item.moment || item.label || item.value || item.insight || item.fix || item.caption || item.idea || item.risk || item.note || JSON.stringify(item)}`}
            {typeof item === 'object' && (item.why || item.whyItMatters || item.reason) && (
              <span className="block text-xs text-gray-500 mt-1">{item.why || item.whyItMatters || item.reason}</span>
            )}
          </li>
        ))}
      </ul>
    </div>
  );
}

function ViralChecklist({ items }) {
  const checklist = asArray(items);
  if (!checklist.length) return null;
  return (
    <div className="rounded-xl border border-gray-800 bg-gray-900 p-5">
      <div className="flex items-center gap-2 text-purple-200 mb-4">
        <ClipboardList size={18} />
        <h3 className="font-semibold">Viral Checklist</h3>
      </div>
      <div className="grid grid-cols-1 gap-3 md:grid-cols-2">
        {checklist.map((item, index) => (
          <div key={`${item.label || index}`} className={`rounded-lg border p-4 ${item.pass ? 'border-green-500/30 bg-green-500/10' : 'border-yellow-500/30 bg-yellow-500/10'}`}>
            <p className={`text-sm font-semibold ${item.pass ? 'text-green-200' : 'text-yellow-100'}`}>
              {item.pass ? 'Pass' : 'Fix'} · {item.label || `Check ${index + 1}`}
            </p>
            {item.note && <p className="mt-2 text-xs leading-relaxed text-gray-300">{item.note}</p>}
          </div>
        ))}
      </div>
    </div>
  );
}

function ReelInsightDetail({ report }) {
  if (!report) {
    return (
      <div className="rounded-xl border border-gray-800 bg-gray-900 p-8 text-center text-sm text-gray-500">
        Select or create a report to view the analysis.
      </div>
    );
  }

  const analysis = report.analysis || {};
  const score = getReportScore(report);
  const breakdown = asArray(analysis.scoreBreakdown || analysis.breakdown);

  return (
    <div className="space-y-5">
      <div className="rounded-xl border border-gray-800 bg-gray-900 p-5">
        <div className="mobile-panel-header flex items-start justify-between gap-4 mb-5">
          <div>
            <p className="text-xs uppercase tracking-wider text-gray-500">{getModeLabel(report.mode)}</p>
            <h2 className="text-2xl font-serif text-white mt-1">{report.title}</h2>
            {report.reel_url && (
              <a href={report.reel_url} target="_blank" rel="noreferrer" className="text-sm text-blue-300 hover:text-blue-200">
                Open Instagram reference
              </a>
            )}
          </div>
          <span className={`rounded-full border px-3 py-1 text-xs ${getStatusClass(report.status)}`}>
            {report.status}
          </span>
        </div>

        <ScoreCircle score={score} mode={report.mode} />

        {(analysis.contentBucket || analysis.hook?.firstFrame || analysis.hook?.notes) && (
          <div className="mt-5 grid grid-cols-1 gap-3 md:grid-cols-2">
            {analysis.contentBucket && (
              <div className="rounded-lg border border-gray-800 bg-gray-950/70 p-4">
                <p className="text-xs uppercase tracking-wider text-gray-500">Content bucket</p>
                <p className="mt-2 text-sm text-gray-200">{analysis.contentBucket}</p>
              </div>
            )}
            {(analysis.hook?.firstFrame || analysis.hook?.notes) && (
              <div className="rounded-lg border border-gray-800 bg-gray-950/70 p-4">
                <p className="text-xs uppercase tracking-wider text-gray-500">Hook diagnosis</p>
                <p className="mt-2 text-sm text-gray-200">{analysis.hook.firstFrame || analysis.hook.notes}</p>
              </div>
            )}
          </div>
        )}

        {(analysis.verdict || analysis.summary || report.error_message) && (
          <div className="mt-5 rounded-lg border border-gray-800 bg-gray-950/70 p-4">
            <p className="text-sm text-gray-300 leading-relaxed">
              {report.error_message || analysis.verdict || analysis.summary}
            </p>
          </div>
        )}
      </div>

      {breakdown.length > 0 && (
        <div className="rounded-xl border border-gray-800 bg-gray-900 p-5">
          <div className="flex items-center gap-2 text-purple-200 mb-4">
            <ClipboardList size={18} />
            <h3 className="font-semibold">Score Breakdown</h3>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            {breakdown.map((item, index) => {
              const itemScore = Number(item.score ?? item.value ?? 0);
              const max = Number(item.max ?? 10);
              const width = Math.max(0, Math.min(100, max ? (itemScore / max) * 100 : 0));
              return (
                <div key={`${item.label || index}`} className="rounded-lg border border-gray-800 bg-gray-950/70 p-4">
                  <div className="flex items-center justify-between gap-3 text-sm">
                    <span className="font-medium text-gray-200">{item.label || item.metric || `Metric ${index + 1}`}</span>
                    <span className="text-purple-200 font-semibold">{itemScore}/{max}</span>
                  </div>
                  <div className="mt-3 h-2 rounded-full bg-gray-800 overflow-hidden">
                    <div className="h-full rounded-full bg-purple-500" style={{ width: `${width}%` }} />
                  </div>
                  {item.reason && <p className="text-xs text-gray-500 mt-3 leading-relaxed">{item.reason}</p>}
                </div>
              );
            })}
          </div>
        </div>
      )}

      <ViralChecklist items={analysis.viralChecklist} />
      <SuggestedCtaCard cta={analysis.suggestedCta} />
      <WhyItWorksCard analysis={analysis} />
      <StrategyReferencesCard references={analysis.strategyReferences} />

      <div className="grid grid-cols-1 xl:grid-cols-2 gap-5">
        <TextList title="Visual Beat Sheet" icon={Film} items={analysis.visualBeatSheet} color="text-pink-200" />
        <TextList title="Key Moments" icon={CheckCircle2} items={analysis.keyMoments} color="text-green-200" />
        <TextList title="Retention Risks" icon={AlertTriangle} items={analysis.retentionRisks} color="text-yellow-200" />
        <TextList title="Specific Fixes" icon={Wand2} items={analysis.specificFixes} color="text-blue-200" />
        <TextList title="Repeatable Formula" icon={RefreshCw} items={analysis.repeatableFormula} color="text-cyan-200" />
        <TextList title="Caption Ideas" icon={ClipboardList} items={analysis.captionIdeas} color="text-purple-200" />
        <TextList title="Next Reel Ideas" icon={Sparkles} items={analysis.nextReelIdeas} color="text-teal-200" />
      </div>

      {analysis.transcript && (
        <div className="rounded-xl border border-gray-800 bg-gray-900 p-5">
          <h3 className="font-semibold text-white mb-3">Transcript</h3>
          <p className="whitespace-pre-wrap text-sm text-gray-300 leading-relaxed">{analysis.transcript}</p>
        </div>
      )}
    </div>
  );
}

function ReelInsightsSection({ session, dashboardId = 'vasia' }) {
  const accessToken = session?.access_token;
  const [title, setTitle] = useState('');
  const [reelUrl, setReelUrl] = useState('');
  const [mode, setMode] = useState('extract_value');
  const [file, setFile] = useState(null);
  const [reports, setReports] = useState([]);
  const [selectedReport, setSelectedReport] = useState(null);
  const [config, setConfig] = useState(null);
  const [status, setStatus] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [deletingId, setDeletingId] = useState('');

  const selectedMode = useMemo(() => reelInsightModes.find((item) => item.key === mode), [mode]);

  const loadReports = async () => {
    if (!accessToken) return;
    setIsLoading(true);
    try {
      const [configResult, listResult] = await Promise.all([
        getReelInsightsConfig({ accessToken, dashboardId }),
        listReelInsights({ accessToken, dashboardId })
      ]);
      setConfig(configResult);
      const nextReports = listResult?.reports || [];
      setReports(nextReports);
      setSelectedReport((current) => {
        if (current) return nextReports.find((item) => item.id === current.id) || nextReports[0] || null;
        return nextReports[0] || null;
      });
    } catch (error) {
      setStatus(error.message);
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    loadReports();
  }, [accessToken, dashboardId]);

  const handleFileChange = (event) => {
    const nextFile = event.target.files?.[0] || null;
    setFile(nextFile);
  };

  const validate = () => {
    if (!accessToken) return 'Sign in with Supabase before using Reel Insights.';
    if (!title.trim()) return 'Add a short title for this reel.';
    if (!file) return 'Upload the MP4, MOV, or WEBM file. Instagram URL alone is only saved as a reference.';
    if (file.size > REEL_MAX_UPLOAD_BYTES) return `Video must be ${formatBytes(REEL_MAX_UPLOAD_BYTES)} or smaller.`;
    if (file.type && !REEL_ALLOWED_TYPES.includes(file.type)) return 'Upload an MP4, MOV, or WEBM file.';
    return '';
  };

  const handleSubmit = async (event) => {
    event.preventDefault();
    const validationError = validate();
    if (validationError) {
      setStatus(validationError);
      return;
    }

    setIsSubmitting(true);
    setStatus('Creating private upload link...');

    try {
      const upload = await createReelUploadUrl({
        accessToken,
        dashboardId,
        title: title.trim(),
        reelUrl: reelUrl.trim(),
        mode,
        file
      });

      setStatus('Uploading video privately...');
      await uploadReelToSignedUrl({ uploadUrl: upload.uploadUrl, file });

      setStatus('Analyzing reel with Gemini. This can take a minute...');
      const analyzed = await analyzeReel({ accessToken, analysisId: upload.analysisId });

      setTitle('');
      setReelUrl('');
      setFile(null);
      const fileInput = document.getElementById('reel-video-upload');
      if (fileInput) fileInput.value = '';

      await loadReports();
      if (analyzed?.report) setSelectedReport(analyzed.report);
      setStatus('Analysis complete.');
    } catch (error) {
      setStatus(error.message);
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleDelete = async (report) => {
    if (!report || !window.confirm(`Delete "${report.title}"?`)) return;
    setDeletingId(report.id);
    setStatus('');
    try {
      await deleteReelInsight({ accessToken, analysisId: report.id });
      const nextReports = reports.filter((item) => item.id !== report.id);
      setReports(nextReports);
      setSelectedReport((current) => current?.id === report.id ? nextReports[0] || null : current);
      setStatus('Report deleted.');
    } catch (error) {
      setStatus(error.message);
    } finally {
      setDeletingId('');
    }
  };

  if (!accessToken) {
    return (
      <div>
        <p className="text-xs text-gray-500 uppercase tracking-wider mb-1">Reel intelligence</p>
        <h2 className="text-2xl font-serif text-white mb-4">Reel Virality Checker</h2>
        <div className="rounded-xl border border-yellow-500/30 bg-yellow-500/10 p-5 text-yellow-100">
          Sign in through Supabase to use Reel Insights. The video upload and Gemini analysis APIs require an authenticated dashboard user.
        </div>
      </div>
    );
  }

  return (
    <div>
      <div className="mobile-panel-header flex items-start justify-between gap-4 mb-6">
        <div>
          <p className="text-xs text-gray-500 uppercase tracking-wider mb-1">Reel intelligence</p>
          <h2 className="text-2xl font-serif text-white mb-1">Reel Virality Checker</h2>
          <p className="text-sm text-gray-400 max-w-3xl">
            Upload a reel file for strict creative analysis. URLs are saved for reference, but Gemini analysis uses the uploaded video.
          </p>
        </div>
        <button
          onClick={loadReports}
          disabled={isLoading}
          className="inline-flex items-center justify-center gap-2 rounded-lg border border-gray-700 bg-gray-800 px-3 py-2 text-sm text-gray-200 hover:bg-gray-700 disabled:opacity-60"
        >
          <RefreshCw size={15} />
          Refresh
        </button>
      </div>

      {config && (!config.geminiConfigured || !config.supabaseConfigured) && (
        <div className="mb-6 rounded-xl border border-yellow-500/30 bg-yellow-500/10 p-4 text-sm text-yellow-100">
          <div className="flex gap-2">
            <AlertTriangle size={17} className="mt-0.5 shrink-0" />
            <p>
              The Virality Checker needs server secrets in Cloudflare: `GEMINI_API_KEY`, `SUPABASE_SERVICE_ROLE_KEY`, and Supabase URL/anon values.
            </p>
          </div>
        </div>
      )}

      <div className="grid grid-cols-1 xl:grid-cols-[420px_minmax(0,1fr)] gap-6">
        <div className="space-y-5">
          <form onSubmit={handleSubmit} className="rounded-xl border border-gray-800 bg-gray-900 p-5">
            <div className="flex items-center gap-2 text-purple-200 mb-4">
              <Upload size={18} />
              <h3 className="font-semibold">New analysis</h3>
            </div>

            <label className="block text-xs text-gray-400 mb-1">Reel title</label>
            <input
              value={title}
              onChange={(event) => setTitle(event.target.value)}
              placeholder="e.g. White dress transition reel"
              className="mb-4 w-full rounded-lg border border-gray-700 bg-gray-800 px-3 py-2 text-sm focus:border-purple-500 focus:outline-none"
            />

            <label className="block text-xs text-gray-400 mb-1">Instagram Reel URL</label>
            <input
              value={reelUrl}
              onChange={(event) => setReelUrl(event.target.value)}
              placeholder="https://www.instagram.com/reel/..."
              className="mb-4 w-full rounded-lg border border-gray-700 bg-gray-800 px-3 py-2 text-sm focus:border-purple-500 focus:outline-none"
            />

            <label className="block text-xs text-gray-400 mb-2">Mode</label>
            <div className="mb-4 grid grid-cols-1 gap-2">
              {reelInsightModes.map((item) => (
                <button
                  key={item.key}
                  type="button"
                  onClick={() => setMode(item.key)}
                  className={`rounded-lg border p-3 text-left transition-colors ${
                    mode === item.key
                      ? 'border-purple-500/60 bg-purple-500/10 text-white'
                      : 'border-gray-700 bg-gray-800 text-gray-300 hover:border-gray-600'
                  }`}
                >
                  <div className="font-medium">{item.label}</div>
                  <div className="mt-1 text-xs text-gray-500">{item.description}</div>
                </button>
              ))}
            </div>

            <label className="block text-xs text-gray-400 mb-1">Video file</label>
            <input
              id="reel-video-upload"
              type="file"
              accept={REEL_ACCEPT}
              onChange={handleFileChange}
              className="mb-3 w-full rounded-lg border border-gray-700 bg-gray-800 px-3 py-2 text-sm text-gray-300 file:mr-3 file:rounded-md file:border-0 file:bg-purple-600 file:px-3 file:py-1.5 file:text-sm file:text-white"
            />
            <p className="mb-4 text-xs text-gray-500">
              MP4, MOV, or WEBM up to {formatBytes(REEL_MAX_UPLOAD_BYTES)}. Selected mode: {selectedMode?.label}.
            </p>

            {file && (
              <div className="mb-4 rounded-lg border border-gray-800 bg-gray-950/70 p-3 text-xs text-gray-400">
                {file.name} · {formatBytes(file.size)}
              </div>
            )}

            <button
              type="submit"
              disabled={isSubmitting}
              className="inline-flex w-full items-center justify-center gap-2 rounded-lg bg-purple-600 px-4 py-2 text-sm font-medium text-white transition-colors hover:bg-purple-500 disabled:bg-gray-700 disabled:text-gray-400"
            >
              {isSubmitting ? <Loader2 size={16} className="animate-spin" /> : <Sparkles size={16} />}
              {isSubmitting ? 'Analyzing...' : 'Analyze reel'}
            </button>

            {status && (
              <div className="mt-4 rounded-lg border border-gray-700 bg-gray-800/80 p-3 text-sm text-gray-300">
                {status}
              </div>
            )}
          </form>

          <ReportList
            reports={reports}
            selectedId={selectedReport?.id}
            onSelect={setSelectedReport}
            onDelete={handleDelete}
            isDeleting={deletingId}
          />
        </div>

        <ReelInsightDetail report={selectedReport} />
      </div>
    </div>
  );
}

export {
  ReelInsightDetail,
  ReportList,
  SuggestedCtaCard,
  WhyItWorksCard,
  StrategyReferencesCard
};

export default ReelInsightsSection;
